import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import moment from "moment";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Row, Col, Label, Input, Table, Spinner } from "reactstrap";
import DocumentosContenedor from "./DocumentosContenedor";
import {
  PASOS, estatusDePasos, MODALIDADES, TAMANOS, ESTATUS_COBRO, ESTATUS_DEMORAS,
  calculaDemoras, semaforoDemoras, calculaTotales, colorEstatus, fmtUSD, fmtMXN, fmtNum, headersAuth,
} from "./revalidacionUtils";

const URL_REV = process.env.REACT_APP_URL_REVALIDACIONES_CONTENEDOR;

const VACIO = {
  estatus: "", ejecutivoChina: "", hbl: "", bl: "", shipper: "", consignatario: "", descripcionMercancia: "",
  bultos: "", peso: "", volumen: "", buque: "", brokerHBL: "", tamano: "", modalidad: "", agenteAduanal: "", cambioAgente: "",
  diasLibres: "", inicioProceso: "", notificacionMBL: "", notificacionAA: "", cartas: "", solicitudPago: "",
  revalidacion: "", fechaEIR: "", cargosLocales: "", servicioML: "", tipoCambio: "", garantia: "", fechaGarantia: "",
  pagado: "", notasCobro: "", facturaNaviera: "", montoDemoras: "", estatusDemoras: "", observaciones: "", cargosExtra: [],
  avisoArribo: "", cartasEnviadas: "", cartasAplicadas: "", pagoConfirmado: "", revalidacionEnviada: "", telexEnviado: "",
  despachado: "", fechaCargaTren: "", montoPagoNaviera: "", monedaPagoNaviera: "USD",
};

const Seccion = ({ icon, titulo, tono, children, extra }) => (
  <div className={`form-section rv-section${tono ? ` rv-${tono}` : ""}`}>
    <div className="form-section-header">
      <i className={icon} />
      <h4 className="form-section-title">{titulo}</h4>
      {extra ? <div style={{ marginLeft: "auto" }}>{extra}</div> : null}
    </div>
    <div className="form-section-body">{children}</div>
  </div>
);

const Campo = ({ label, children, md = 3 }) => (
  <Col md={md} className="mb-2">
    <Label>{label}</Label>
    {children}
  </Col>
);

const Dato = ({ label, valor }) => (
  <div className="rv-dato">
    <span>{label}</span>
    <strong>{valor || "—"}</strong>
  </div>
);

/**
 *  props: isOpen, toggle, contenedorId, modo ("datos" | "cobros"), puedeEditar, puedeSubir, onSaved(fila),
 *         alHistorial (al abrir baja directo al historial)
 */
export default function RevalidacionModal({ isOpen, toggle, contenedorId, modo = "datos", puedeEditar, puedeSubir, onSaved, alHistorial }) {
  const [fila, setFila] = useState(null);
  const [form, setForm] = useState(VACIO);
  const [guardando, setGuardando] = useState(false);
  const [tcDia, setTcDia] = useState(null);
  const [pagosAgente, setPagosAgente] = useState({}); // id del cambio → { monto, moneda } en captura
  const [agentes, setAgentes] = useState([]);
  const refHistorial = useRef(null);
  const irHistorial = () => refHistorial.current && refHistorial.current.scrollIntoView({ block: "start" });

  useEffect(() => {
    if (fila && alHistorial) setTimeout(irHistorial, 350);
  }, [fila, alHistorial]);

  // Tipo de cambio vigente (el mismo del sidebar) para llenar el TC con un clic
  useEffect(() => {
    if (!isOpen || modo !== "cobros") return;
    axios
      .get(`${process.env.REACT_APP_URL_TIPOS_CAMBIO}Vigente`, { headers: headersAuth() })
      .then(({ data }) => setTcDia(data))
      .catch(() => setTcDia(null));
  }, [isOpen, modo]);

  useEffect(() => {
    if (!isOpen) return;
    axios
      .get(process.env.REACT_APP_URL_AGENTES_ADUANALES, { headers: headersAuth() })
      .then(({ data }) => setAgentes(data.filter((a) => a.is_active !== "No").map((a) => a.name)))
      .catch(() => setAgentes([]));
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !contenedorId) return;
    setFila(null);
    axios
      .get(`${URL_REV}/${contenedorId}`, { headers: headersAuth() })
      .then(({ data }) => {
        setFila(data);
        const r = data.revalidacion || {};
        const base = { ...VACIO };
        Object.keys(VACIO).forEach((k) => {
          if (r[k] !== undefined && r[k] !== null) base[k] = r[k];
        });
        if (r.diasLibres === null || r.diasLibres === undefined) base.diasLibres = "";
        if (!r._id) base.buque = data.contenedor.buque || "";
        base.cargosExtra = (r.cargosExtra || []).map((x) => ({ ...x }));
        setForm(base);
      })
      .catch((err) => {
        Swal.fire({ icon: "error", title: "No se pudo cargar", text: err?.response?.data?.message || err.message });
        toggle();
      });
  }, [isOpen, contenedorId]); // eslint-disable-line

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setExtra = (i, k, v) => setForm((f) => ({ ...f, cargosExtra: f.cargosExtra.map((x, j) => (j === i ? { ...x, [k]: v } : x)) }));
  const addExtra = () => setForm((f) => ({ ...f, cargosExtra: [...f.cargosExtra, { concepto: "", monto: "", servicioML: "", cobrado: "No", pagadoBroker: "No" }] }));
  const delExtra = (i) => setForm((f) => ({ ...f, cargosExtra: f.cargosExtra.filter((_, j) => j !== i) }));

  const c = fila ? fila.contenedor : {};
  const diasLibresEfectivos = form.diasLibres !== "" ? form.diasLibres : fila ? fila.diasLibresBase : null;
  const enTren = fila && fila.inicioDiasLibres === "Tren";
  const inicioDias = enTren ? form.fechaCargaTren : c.eta;
  // Entregado o con EIR: ya no es demora vencida
  const cerrada = !!fila && (c.surtido === "Si" || !!c.fechaEntregaReal || (c.docs && c.docs.EIR === "Si") || !!form.fechaEIR);
  const demoras = useMemo(() => calculaDemoras(inicioDias, diasLibresEfectivos, form.fechaEIR, cerrada), [inicioDias, diasLibresEfectivos, form.fechaEIR, cerrada]);
  const estatus = form.estatus === "DETENIDO" ? "DETENIDO" : estatusDePasos(form) || (cerrada ? "Cerrado" : "");
  const semaforo = semaforoDemoras(demoras, form.fechaEIR);
  const totales = useMemo(() => calculaTotales(form), [form]);
  const pasosHechos = PASOS.filter((p) => form[p.campo]).length;

  const guardar = async () => {
    setGuardando(true);
    try {
      const { data } = await axios.put(`${URL_REV}/${contenedorId}`, form, { headers: headersAuth() });
      Swal.fire({ icon: "success", title: "Revalidación guardada", timer: 1300, showConfirmButton: false });
      onSaved && onSaved(data);
      toggle();
    } catch (err) {
      Swal.fire({ icon: "error", title: "No se pudo guardar", text: err?.response?.data?.message || err.message });
    }
    setGuardando(false);
  };

  const ro = !puedeEditar;
  const agenteGuardado = (fila && fila.revalidacion && fila.revalidacion.agenteAduanal) || "";
  const cambiosAgente = (fila && fila.revalidacion && fila.revalidacion.cambiosAgente) || [];
  const pagosPendientes = cambiosAgente.filter((x) => x.pagado !== "Si").length;
  const pagoAgente = (x) => pagosAgente[x._id] || { monto: x.monto || "", moneda: x.moneda || "USD", fechaPago: x.fechaPago || moment().format("YYYY-MM-DD") };

  const guardaPagoAgente = async (cambio, pagado) => {
    const pago = pagoAgente(cambio);
    if (pagado === "Si" && (!pago.fechaPago || !(Number(pago.monto) > 0))) {
      Swal.fire({ icon: "warning", title: "Captura el monto y la fecha del pago" });
      return;
    }
    try {
      const { data } = await axios.put(
        `${URL_REV}/${contenedorId}`,
        { pagoCambioAgente: { id: cambio._id, pagado, fechaPago: pago.fechaPago, monto: pago.monto, moneda: pago.moneda } },
        { headers: headersAuth() }
      );
      setFila((f) => ({ ...data, historialContenedor: f.historialContenedor }));
      setPagosAgente((p) => { const { [cambio._id]: _, ...resto } = p; return resto; });
      onSaved && onSaved(data);
    } catch (err) {
      Swal.fire({ icon: "error", title: "No se pudo guardar", text: err?.response?.data?.message || err.message });
    }
  };
  // Revalidación + contenedor (ShipsGo, documentos, edición), lo más nuevo primero
  const historial = useMemo(() => {
    if (!fila) return [];
    const propios = (fila.revalidacion && fila.revalidacion.historial) || [];
    return [...propios].reverse().concat(fila.historialContenedor || []).sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
  }, [fila]);

  return (
    <Modal size="xl" isOpen={isOpen} toggle={toggle} className="rv-modal">
      <ModalHeader toggle={toggle}>
        <span className="rv-modal-title">
          <i className={modo === "cobros" ? "fas fa-file-invoice-dollar" : "fas fa-stamp"} />
          {modo === "cobros" ? "Cargos y cobros" : "Revalidación"} · <strong>{c.referencia || "…"}</strong>
          {c.numeroReferencia ? <small>{c.numeroReferencia}</small> : null}
          {fila ? (
            <Button size="sm" color="secondary" className="rv-btn-historial" onClick={irHistorial}>
              <i className="fas fa-history" /> Historial ({historial.length})
            </Button>
          ) : null}
        </span>
      </ModalHeader>
      <ModalBody>
        {!fila ? (
          <div className="rc-state"><Spinner /> <div>Cargando…</div></div>
        ) : (
          <>
            {/* Resumen del contenedor */}
            <div className="rv-resumen">
              <Dato label="Cliente" valor={c.cliente} />
              <Dato label="Naviera" valor={c.naviera} />
              <Dato label="Broker" valor={c.broker} />
              <Dato label="ETD" valor={c.etd} />
              <Dato label="ETA" valor={c.eta} />
              <Dato label="POL → POD" valor={[c.pol, c.pod].filter(Boolean).join(" → ")} />
              <div className="rv-dato">
                <span>Estatus</span>
                <strong><span className={`badge-soft badge-soft-${colorEstatus(estatus)}`}>{estatus || "Sin iniciar"}</span></strong>
                {!ro ? (
                  <label className="rv-detenido">
                    <input
                      type="checkbox"
                      checked={form.estatus === "DETENIDO"}
                      onChange={(e) => setForm((f) => ({ ...f, estatus: e.target.checked ? "DETENIDO" : estatusDePasos(f) }))}
                    /> Detenido
                  </label>
                ) : null}
              </div>
            </div>

            {modo === "datos" ? (
              <>
                <Seccion icon="fas fa-ship" titulo="Embarque">
                  <Row>
                    <Campo label="Ejecutivo China"><Input bsSize="sm" value={form.ejecutivoChina} onChange={set("ejecutivoChina")} disabled={ro} /></Campo>
                    <Campo label="HBL (número)"><Input bsSize="sm" value={form.hbl} onChange={set("hbl")} disabled={ro} /></Campo>
                    <Campo label="BL / MBL (número)"><Input bsSize="sm" value={form.bl} onChange={set("bl")} disabled={ro} /></Campo>
                    <Campo label="Agente aduanal">
                      <Input bsSize="sm" type="select" value={form.agenteAduanal} onChange={set("agenteAduanal")} disabled={ro || (!!agenteGuardado && form.cambioAgente !== "Si")}>
                        <option value="">Selecciona</option>
                        {form.agenteAduanal && !agentes.includes(form.agenteAduanal) ? <option>{form.agenteAduanal}</option> : null}
                        {agentes.map((x) => <option key={x}>{x}</option>)}
                      </Input>
                      {agenteGuardado && !ro ? (
                        <label className="rv-detenido">
                          <input
                            type="checkbox"
                            checked={form.cambioAgente === "Si"}
                            onChange={(e) => setForm((f) => ({ ...f, cambioAgente: e.target.checked ? "Si" : "", agenteAduanal: e.target.checked ? f.agenteAduanal : agenteGuardado }))}
                          /> Va a cambiar de agente
                        </label>
                      ) : null}
                    </Campo>
                  </Row>
                </Seccion>

                {cambiosAgente.length ? (
                  <Seccion icon="fas fa-exchange-alt" titulo="Cambios de agente aduanal" extra={<span className={`badge-soft badge-soft-${pagosPendientes ? "danger" : "success"}`}>{pagosPendientes ? `${pagosPendientes} por pagar a naviera` : "Pagados"}</span>}>
                    <Table size="sm">
                      <thead>
                        <tr><th>Fecha</th><th>De</th><th>A</th><th>Hizo el cambio</th><th className="td-num">Monto</th><th>Moneda</th><th>Fecha de pago</th><th>Pago a naviera</th><th></th></tr>
                      </thead>
                      <tbody>
                        {cambiosAgente.map((x) => {
                          const pago = pagoAgente(x);
                          const pagado = x.pagado === "Si";
                          return (
                            <tr key={x._id}>
                              <td>{x.fecha}</td>
                              <td>{x.anterior}</td>
                              <td className="td-strong">{x.nuevo}</td>
                              <td>{x.usuario}</td>
                              <td style={{ width: 110 }}><Input bsSize="sm" type="number" min="0" step="any" value={pago.monto} disabled={ro || pagado} onChange={(e) => setPagosAgente((p) => ({ ...p, [x._id]: { ...pago, monto: e.target.value } }))} /></td>
                              <td style={{ width: 90 }}>
                                <Input bsSize="sm" type="select" value={pago.moneda} disabled={ro || pagado} onChange={(e) => setPagosAgente((p) => ({ ...p, [x._id]: { ...pago, moneda: e.target.value } }))}>
                                  <option>USD</option><option>MXN</option>
                                </Input>
                              </td>
                              <td style={{ width: 140 }}><Input bsSize="sm" type="date" value={pago.fechaPago} disabled={ro || pagado} onChange={(e) => setPagosAgente((p) => ({ ...p, [x._id]: { ...pago, fechaPago: e.target.value } }))} /></td>
                              <td><span className={`badge-soft badge-soft-${pagado ? "success" : "danger"}`}>{pagado ? "Pagado" : "Pendiente"}</span></td>
                              <td>
                                {ro ? null : pagado ? (
                                  <Button size="sm" color="secondary" onClick={() => guardaPagoAgente(x, "No")}>Deshacer</Button>
                                ) : (
                                  <Button size="sm" color="success" onClick={() => guardaPagoAgente(x, "Si")}><i className="fas fa-check" /> Pagado</Button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </Seccion>
                ) : null}

                <Seccion icon="fas fa-file-contract" titulo="Datos del HBL" tono="hbl" extra={<span className="rv-tag-hbl">Se toman del HBL</span>}>
                  <Row>
                    <Campo label="Shipper" md={4}><Input bsSize="sm" value={form.shipper} onChange={set("shipper")} disabled={ro} /></Campo>
                    <Campo label="Consignatario" md={4}><Input bsSize="sm" value={form.consignatario} onChange={set("consignatario")} disabled={ro} /></Campo>
                    <Campo label="Broker (HBL)" md={4}><Input bsSize="sm" value={form.brokerHBL} onChange={set("brokerHBL")} disabled={ro} /></Campo>
                    <Campo label="Descripción de la mercancía" md={4}><Input bsSize="sm" value={form.descripcionMercancia} onChange={set("descripcionMercancia")} disabled={ro} /></Campo>
                    <Campo label="Buque" md={4}><Input bsSize="sm" value={form.buque} onChange={set("buque")} disabled={ro} /></Campo>
                    <Campo label="Bultos" md={2}><Input bsSize="sm" type="number" value={form.bultos} onChange={set("bultos")} disabled={ro} /></Campo>
                    <Campo label="Peso (kg)" md={2}><Input bsSize="sm" type="number" value={form.peso} onChange={set("peso")} disabled={ro} /></Campo>
                    <Campo label="Volumen (m³)" md={2}><Input bsSize="sm" type="number" value={form.volumen} onChange={set("volumen")} disabled={ro} /></Campo>
                    <Campo label="Tamaño" md={2}>
                      <Input bsSize="sm" type="select" value={form.tamano} onChange={set("tamano")} disabled={ro}>
                        <option value="">—</option>
                        {TAMANOS.map((x) => <option key={x}>{x}</option>)}
                      </Input>
                    </Campo>
                    <Campo label="Modalidad" md={2}>
                      <Input bsSize="sm" type="select" value={form.modalidad} onChange={set("modalidad")} disabled={ro}>
                        <option value="">—</option>
                        {MODALIDADES.map((x) => <option key={x}>{x}</option>)}
                      </Input>
                    </Campo>
                  </Row>
                </Seccion>

                <Seccion icon="fas fa-tasks" titulo="Proceso de revalidación" extra={<span className="rv-pasos-txt">{pasosHechos} / {PASOS.length}</span>}>
                  <div className="rv-stepper">
                    {PASOS.map((p, i) => (
                      <div key={p.campo} className={`rv-step${form[p.campo] ? " done" : ""}`}>
                        <span className="rv-step-dot">{form[p.campo] ? <i className="fas fa-check" /> : i + 1}</span>
                        <span className="rv-step-label" title={p.label}>{p.estatus}</span>
                        <span className="rv-step-date">{form[p.campo] || "—"}</span>
                      </div>
                    ))}
                  </div>
                  <Row>
                    {PASOS.map((p) => (
                      <Campo key={p.campo} label={p.label} md={3}>
                        <Input bsSize="sm" type="date" value={form[p.campo]} onChange={set(p.campo)} disabled={ro} />
                      </Campo>
                    ))}
                  </Row>
                  <div className="td-muted" style={{ fontSize: "0.7rem" }}>
                    El estatus avanza solo con el último paso que tenga fecha. El EIR recibido cierra las demoras.
                  </div>
                </Seccion>

                <Seccion icon="fas fa-hourglass-half" titulo="Demoras" extra={<span className={`badge-soft badge-soft-${semaforo.color}`}>{semaforo.texto}</span>}>
                  <Row className="align-items-end">
                    <Campo
                      label={`Días libres (${fila.origenDiasLibresBase === "Cliente" ? "contrato del cliente" : "naviera"}: ${fila.diasLibresBase ?? "sin configurar"})`}
                      md={3}
                    >
                      <Input bsSize="sm" type="number" placeholder={fila.diasLibresBase ?? ""} value={form.diasLibres} onChange={set("diasLibres")} disabled={ro} />
                    </Campo>
                    {enTren ? (
                      <Campo label="Carga en tren" md={3}>
                        <Input bsSize="sm" type="date" value={form.fechaCargaTren} onChange={set("fechaCargaTren")} disabled={ro} />
                      </Campo>
                    ) : null}
                    <Col md={enTren ? 6 : 9} className="mb-2">
                      <div className="rv-demoras">
                        <Dato label={enTren ? "Inicio (carga en tren)" : "ETA"} valor={enTren ? form.fechaCargaTren || "Pendiente" : c.eta} />
                        <Dato label="Deadline demoras" valor={demoras.deadline} />
                        <Dato label="Corte de demoras" valor={demoras.corte || (form.fechaEIR || cerrada ? "—" : "Corre a hoy")} />
                        <Dato label="Demoras generadas" valor={`${fmtNum(demoras.diasDemora)} días`} />
                      </div>
                    </Col>
                  </Row>
                  <div className="td-muted" style={{ fontSize: "0.7rem" }}>
                    Deadline = {enTren ? "carga en tren" : "ETA"} + días libres. Las demoras se cuentan solas a partir del deadline y se cortan con la fecha del EIR.
                    Un contenedor entregado o con EIR ya no cuenta como demora vencida.
                  </div>
                </Seccion>

                <Seccion icon="fas fa-folder-open" titulo="Documentos">
                  <DocumentosContenedor
                    contenedorId={c._id}
                    docs={c.docs}
                    puedeSubir={puedeSubir}
                    onChange={(docs) => setFila((f) => ({ ...f, contenedor: { ...f.contenedor, docs } }))}
                  />
                </Seccion>

                <Seccion icon="fas fa-sticky-note" titulo="Observaciones">
                  <Input type="textarea" rows={2} value={form.observaciones} onChange={set("observaciones")} disabled={ro} />
                </Seccion>
              </>
            ) : (
              <>
                <Seccion icon="fas fa-dollar-sign" titulo="Cargos locales y servicio (USD)" tono="cobros">
                  <Row>
                    <Campo label="Cargos locales cobrados"><Input bsSize="sm" type="number" value={form.cargosLocales} onChange={set("cargosLocales")} disabled={ro} /></Campo>
                    <Campo label="Servicio ML Freight"><Input bsSize="sm" type="number" value={form.servicioML} onChange={set("servicioML")} disabled={ro} /></Campo>
                    <Campo label="Tipo de cambio">
                      <Input bsSize="sm" type="number" value={form.tipoCambio} onChange={set("tipoCambio")} disabled={ro} />
                      {tcDia && !ro && Number(form.tipoCambio) !== tcDia.tipoCambio ? (
                        <button type="button" className="rv-usar-tc" onClick={() => setForm((f) => ({ ...f, tipoCambio: tcDia.tipoCambio }))}>
                          <i className="fas fa-magic" /> Usar TC del {tcDia.fecha}: ${Number(tcDia.tipoCambio).toFixed(4)}
                        </button>
                      ) : null}
                    </Campo>
                    <Campo label="Factura naviera"><Input bsSize="sm" value={form.facturaNaviera} onChange={set("facturaNaviera")} disabled={ro} /></Campo>
                  </Row>
                  <div className="rv-totales">
                    <Dato label="Total USD" valor={fmtUSD(totales.total)} />
                    <Dato label="Total con IVA USD" valor={fmtUSD(totales.totalIVA)} />
                    <Dato label="Total MXN" valor={totales.totalMXN ? fmtMXN(totales.totalMXN) : "Captura TC"} />
                  </div>
                  <Row className="mt-2">
                    <Campo label="Garantía"><Input bsSize="sm" type="number" value={form.garantia} onChange={set("garantia")} disabled={ro} /></Campo>
                    <Campo label="Fecha garantía"><Input bsSize="sm" type="date" value={form.fechaGarantia} onChange={set("fechaGarantia")} disabled={ro} /></Campo>
                    <Campo label="Estatus de cobro">
                      <Input bsSize="sm" type="select" value={form.pagado} onChange={set("pagado")} disabled={ro}>
                        {[...new Set([...ESTATUS_COBRO, form.pagado])].map((x) => <option key={x} value={x}>{x || "—"}</option>)}
                      </Input>
                    </Campo>
                    <Campo label="Notas de cobro"><Input bsSize="sm" value={form.notasCobro} onChange={set("notasCobro")} disabled={ro} /></Campo>
                  </Row>
                </Seccion>

                <Seccion icon="fas fa-money-check-alt" titulo="Pago a naviera">
                  <Row>
                    <Campo label="Monto a pagar">
                      <Input bsSize="sm" type="number" step="0.01" value={form.montoPagoNaviera} onChange={set("montoPagoNaviera")} disabled={ro} />
                    </Campo>
                    <Campo label="Moneda" md={2}>
                      <Input bsSize="sm" type="select" value={form.monedaPagoNaviera} onChange={set("monedaPagoNaviera")} disabled={ro}>
                        <option>USD</option><option>MXN</option>
                      </Input>
                    </Campo>
                    <Campo label="Solicitud de pago"><Input bsSize="sm" type="date" value={form.solicitudPago} onChange={set("solicitudPago")} disabled={ro} /></Campo>
                    <Campo label="Pago confirmado" md={4}><Input bsSize="sm" type="date" value={form.pagoConfirmado} onChange={set("pagoConfirmado")} disabled={ro} /></Campo>
                  </Row>
                  <div className="td-muted" style={{ fontSize: "0.7rem" }}>
                    Con solicitud de pago y sin confirmar aparece en Revalidaciones → Pagos a naviera.
                  </div>
                </Seccion>

                <Seccion
                  icon="fas fa-plus-circle"
                  titulo="Cargos extra (USD)"
                  tono="extra"
                  extra={!ro ? <Button size="sm" color="success" onClick={addExtra}><i className="fas fa-plus" /> Agregar</Button> : null}
                >
                  {form.cargosExtra.length === 0 ? (
                    <div className="td-muted">Sin cargos extra (cambio de agente aduanal, almacenaje, etc.)</div>
                  ) : (
                    <Table size="sm" className="rv-table">
                      <thead>
                        <tr>
                          <th>Concepto</th><th className="td-num">Monto</th><th className="td-num">Servicio ML</th>
                          <th className="td-num">Subtotal</th><th className="td-num">Total IVA</th><th>Cobrado</th><th>Pagado x broker</th><th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {form.cargosExtra.map((x, i) => (
                          <tr key={i}>
                            <td><Input bsSize="sm" value={x.concepto} onChange={(e) => setExtra(i, "concepto", e.target.value)} disabled={ro} /></td>
                            <td style={{ width: 110 }}><Input bsSize="sm" type="number" value={x.monto} onChange={(e) => setExtra(i, "monto", e.target.value)} disabled={ro} /></td>
                            <td style={{ width: 110 }}><Input bsSize="sm" type="number" value={x.servicioML} onChange={(e) => setExtra(i, "servicioML", e.target.value)} disabled={ro} /></td>
                            <td className="td-num td-money">{fmtUSD(totales.extras[i]?.subtotal)}</td>
                            <td className="td-num td-money">{fmtUSD(totales.extras[i]?.totalIVA)}</td>
                            <td style={{ width: 90 }}>
                              <Input bsSize="sm" type="select" value={x.cobrado} onChange={(e) => setExtra(i, "cobrado", e.target.value)} disabled={ro}>
                                <option>No</option><option>Si</option>
                              </Input>
                            </td>
                            <td style={{ width: 90 }}>
                              <Input bsSize="sm" type="select" value={x.pagadoBroker} onChange={(e) => setExtra(i, "pagadoBroker", e.target.value)} disabled={ro}>
                                <option>No</option><option>Si</option>
                              </Input>
                            </td>
                            <td>{!ro ? <Button size="sm" color="danger" title="Quitar" onClick={() => delExtra(i)}><i className="fas fa-trash-alt" /></Button> : null}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Seccion>

                <Seccion icon="fas fa-hourglass-half" titulo="Demoras por cobrar (USD)" extra={<span className={`badge-soft badge-soft-${semaforo.color}`}>{semaforo.texto}</span>}>
                  <Row className="align-items-end">
                    <Campo label="Monto demoras"><Input bsSize="sm" type="number" value={form.montoDemoras} onChange={set("montoDemoras")} disabled={ro} /></Campo>
                    <Campo label="Estatus">
                      <Input bsSize="sm" type="select" value={form.estatusDemoras} onChange={set("estatusDemoras")} disabled={ro}>
                        {ESTATUS_DEMORAS.map((x) => <option key={x} value={x}>{x || "—"}</option>)}
                      </Input>
                    </Campo>
                    <Col md={6} className="mb-2">
                      <div className="rv-demoras">
                        <Dato label="Días de demora" valor={fmtNum(demoras.diasDemora)} />
                        <Dato label="Total con IVA" valor={fmtUSD(totales.demorasIVA)} />
                      </div>
                    </Col>
                  </Row>
                </Seccion>

                <div className="rv-gran-total">
                  <span>Total a cobrar (con IVA)</span>
                  <strong>{fmtUSD(totales.granTotal)}</strong>
                  <small>Cargos {fmtUSD(totales.totalIVA)} · Extras {fmtUSD(totales.totalExtras)} · Demoras {fmtUSD(totales.demorasIVA)}</small>
                </div>
              </>
            )}

            <div ref={refHistorial}>
            <Seccion icon="fas fa-history" titulo={`Historial (${historial.length})`}>
              {historial.length === 0 ? (
                <div className="td-muted">Aún no hay cambios registrados.</div>
              ) : (
                <div className="rv-historial">
                  {historial.map((h, i) => (
                    <div key={i} className="rv-hist">
                      <span className="rv-hist-fecha">{moment(h.fecha).format("YYYY-MM-DD HH:mm")}</span>
                      <span className="rv-hist-campo">{h.campo}</span>
                      <span className="rv-hist-cambio">
                        {h.anterior ? <del>{h.anterior}</del> : null}
                        {h.anterior ? " → " : ""}
                        <b>{h.nuevo || "—"}</b>
                      </span>
                      <span className={`rv-hist-user${h.usuario === "Sistema" ? " sistema" : ""}`}>{h.usuario}</span>
                    </div>
                  ))}
                </div>
              )}
            </Seccion>
            </div>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <span className={`badge-soft badge-soft-${colorEstatus(estatus)}`} style={{ marginRight: "auto" }}>{estatus || "Sin iniciar"}</span>
        <Button color="secondary" onClick={toggle}><i className="fas fa-times" /> Cerrar</Button>
        {puedeEditar ? (
          <Button color="success" onClick={guardar} disabled={!fila || guardando}>
            {guardando ? <Spinner size="sm" /> : <i className="fas fa-save" />} Guardar
          </Button>
        ) : null}
      </ModalFooter>
    </Modal>
  );
}
